import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLang } from "@/hooks/useLang";
import { UI, SERVICES, serviceUrl, serviceTitle } from "@/content/site";

/**
 * Client-side 404 page.
 *
 * NB: This still returns a 200 status because the CRA static host does not
 * distinguish routes. We compensate by:
 *   1. `noindex,follow` robots meta so search engines drop the URL,
 *   2. verbose in-body copy so accidental crawlers see clearly that this is
 *      an error state (some engines detect soft-404s heuristically),
 *   3. surfacing real practice-area links + a phone CTA so lost users don't
 *      bounce.
 */
export default function NotFound() {
  const lang = useLang();
  const ui = UI[lang];
  const title = lang === "es"
    ? "Página no encontrada (404) | Garcia-Windsor, P.C."
    : "Page not found (404) | Garcia-Windsor, P.C.";
  return (
    <div className="gw-container gw-section" style={{ textAlign: "left", maxWidth: 780 }}>
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="noindex,follow" />
        <meta httpEquiv="status" content="404" />
      </Helmet>
      <span className="gw-eyebrow">404</span>
      <h1>
        {lang === "es"
          ? "Página no encontrada"
          : "Page not found"}
      </h1>
      <p style={{ fontSize: "1.15rem", color: "var(--gw-text-soft)" }}>
        {lang === "es"
          ? "La página que busca no existe o fue movida. Podría estar disponible en nuestro nuevo sitio bajo una URL diferente."
          : "The page you're looking for doesn't exist or was moved. It may be available on our new site at a different URL."}
      </p>

      <div style={{ display: "flex", gap: "var(--gw-space-3)", flexWrap: "wrap", margin: "var(--gw-space-6) 0" }}>
        <Link to={lang === "es" ? "/es" : "/"} className="gw-btn gw-btn-primary" data-testid="notfound-home-cta">
          {lang === "es" ? "Ir al inicio" : "Go home"}
        </Link>
        <Link to={ui.contactUrl} className="gw-btn gw-btn-accent" data-testid="notfound-contact-cta">
          {ui.ctaConsultation}
        </Link>
        <a href="tel:+12144666699" className="gw-btn gw-btn-outline" data-testid="notfound-call-cta">
          {ui.ctaCall}
        </a>
      </div>

      <h2 style={{ marginTop: "var(--gw-space-8)" }}>
        {lang === "es" ? "Áreas de práctica de derecho familiar" : "Family Law Practice Areas"}
      </h2>
      <ul style={{ columns: 2, columnGap: "var(--gw-space-8)", padding: 0, listStyle: "none" }}>
        {SERVICES.map((s) => (
          <li key={s.key} style={{ padding: "6px 0" }}>
            <Link to={serviceUrl(s, lang)}>{serviceTitle(s, lang)}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
